// src/components/Layout.js
// Shell de l'app : sidebar (rail dockable sur desktop, drawer swipeable sur mobile) + contenu.
// Mobile : barre du haut native (burger + titre + thème + avatar), pas de burger flottant.
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import AppSidebar, { useSidebarWidth } from "./shell/AppSidebar";
import MobileTopBar from "./shell/MobileTopBar";
import UserMenu from "./shell/UserMenu";
import { PageNavProvider, PageNavDrawer } from "./shell/PageNav";
import useDrawerSwipe from "../hooks/useDrawerSwipe";
import useHideOnScroll from "../hooks/useHideOnScroll";

const DESKTOP_MIN = 1024; // = breakpoint 'lg' de Tailwind
const DRAWER_MAX = 288;
const DRAWER_VW = 0.86;

function drawerWidthFor(vw) {
  return Math.round(Math.min(DRAWER_MAX, vw * DRAWER_VW));
}

function useViewport() {
  const read = () => {
    const vw = typeof window !== "undefined" ? window.innerWidth : DESKTOP_MIN;
    return { isDesktop: vw >= DESKTOP_MIN, drawerW: drawerWidthFor(vw) };
  };
  const [vp, setVp] = useState(read);
  useEffect(() => {
    const onResize = () => setVp(read());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return vp;
}

export default function Layout({ children }) {
  const location = useLocation();
  const sidebarWidth = useSidebarWidth();
  const { isDesktop, drawerW } = useViewport();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isBare =
    location.pathname === "/sso" ||
    location.pathname.startsWith("/sso/") ||
    location.pathname === "/login" ||
    location.pathname === "/register";

  const openDrawer = useCallback(() => setMobileOpen(true), []);
  const closeDrawer = useCallback(() => setMobileOpen(false), []);

  // Barre du haut masquée au scroll. L'état vit ici : il ne pilote pas que la
  // barre, il remet aussi à zéro la hauteur qu'elle réserve au contenu.
  const topbarHidden = useHideOnScroll(!isDesktop && !isBare && !mobileOpen);

  // Swipe depuis le bord gauche pour ouvrir, vers la gauche pour fermer.
  const dragOffset = useDrawerSwipe({
    open: mobileOpen,
    onOpen: openDrawer,
    onClose: closeDrawer,
    width: drawerW,
    enabled: !isDesktop && !isBare,
  });

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Drawer ouvert : la page derrière ne doit pas défiler (comportement natif).
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  // Quand la largeur de sidebar change, les pages qui calent des éléments fixes
  // (ex: composer du chat via getBoundingClientRect) doivent se recalculer.
  // On émet un 'resize' au changement + après la transition (300ms).
  useEffect(() => {
    const fire = () => window.dispatchEvent(new Event("resize"));
    fire();
    const t = setTimeout(fire, 320);
    return () => clearTimeout(t);
  }, [sidebarWidth]);

  if (isBare) {
    return <main className="min-h-screen fm-app-bg">{children}</main>;
  }

  return (
    <PageNavProvider>
    <div
      data-topbar-hidden={topbarHidden ? "true" : "false"}
      className="fm-shell min-h-screen w-full max-w-full overflow-x-clip fm-app-bg text-slate-800 dark:text-slate-100"
    >
      <AppSidebar
        mobileOpen={mobileOpen}
        onCloseMobile={closeDrawer}
        dragOffset={dragOffset}
        drawerWidth={drawerW}
        isDesktop={isDesktop}
      />

      {/* Profil + thème en haut à droite — desktop uniquement. */}
      <UserMenu />

      {/* Barre du haut mobile : overlay fixe, s'efface au scroll vers le bas.
          Le drawer s'ouvre aussi au swipe depuis le bord gauche. */}
      <MobileTopBar onOpenMenu={openDrawer} hidden={topbarHidden} />

      {/* Sous-menu de page (« 2e sidebar ») : coulisse depuis la droite. */}
      <PageNavDrawer />

      {/* Colonne contenu : décalée à droite de la sidebar sur desktop. */}
      <div
        className="flex flex-col h-[100dvh] min-w-0 max-w-full overflow-x-clip transition-[margin] duration-300 ease-out"
        style={{ marginLeft: isDesktop ? sidebarWidth : 0 }}
      >
        <main className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-clip">
          {children}
        </main>
      </div>
    </div>
    </PageNavProvider>
  );
}
